import React from 'react';

/**
 * Quick reply buttons for chat messages
 * Handles different types: category, subcategory, existing, action, auth, default
 */
const ChatQuickReplies = ({ 
  replies, 
  onQuickReply, 
  onCategorySelect, 
  onSubCategorySelect, 
  onReviewAction 
}) => {
  if (!replies || replies.length === 0) return null;

  const replyType = replies[0]?.type;

  // Category cards
  if (replyType === 'category') {
    return (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        {replies.map((reply, i) => (
          <button
            key={i}
            onClick={() => onCategorySelect(reply.value)}
            className="group text-left p-4 bg-white hover:bg-gradient-to-br hover:from-indigo-50 hover:to-blue-50 border-2 border-gray-200 hover:border-gray-400 rounded-xl transition-all hover:shadow-lg hover:scale-[1.02] shadow-sm"
          >
            <div className="flex items-start gap-3">
              <div className="text-3xl group-hover:scale-110 transition-transform">
                {reply.icon}
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 mb-1">
                  {reply.text}
                </div>
                <div className="text-xs text-gray-600 leading-relaxed">
                  {reply.desc}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  }

  // Sub-category cards
  if (replyType === 'subcategory') {
    return (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl">
        {replies.map((reply, i) => (
          <button
            key={i}
            onClick={() => onSubCategorySelect(reply.value)}
            className="group text-left p-3 bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-2 border-blue-200 hover:border-blue-400 rounded-xl transition-all hover:shadow-lg hover:scale-[1.02] shadow-sm"
          >
            <div className="flex items-start gap-2">
              <div className="text-2xl group-hover:scale-110 transition-transform">
                {reply.icon}
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 text-sm mb-0.5">
                  {reply.text}
                </div>
                <div className="text-xs text-gray-600 leading-snug">
                  {reply.desc}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  }

  // Existing customer check buttons
  if (replyType === 'existing') {
    return (
      <div className="mt-3 flex flex-col sm:flex-row gap-3 max-w-md">
        {replies.map((reply, i) => (
          <button
            key={i}
            onClick={() => onQuickReply(reply.value)}
            className={`flex-1 px-6 py-4 rounded-2xl text-sm font-bold transition-all hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2 ${
              reply.value === 'YES'
                ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 border-2 border-emerald-400'
                : 'bg-white text-gray-900 hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400'
            }`}
          >
            <span className="text-xl">{reply.icon}</span>
            {reply.text}
          </button>
        ))}
      </div>
    );
  }

  // Review action buttons
  if (replyType === 'action') {
    return (
      <div className="mt-3 flex flex-col sm:flex-row gap-3 max-w-md">
        {replies.map((reply, i) => (
          <button
            key={i}
            onClick={() => onReviewAction(reply.value)}
            className={`flex-1 px-6 py-4 rounded-2xl text-sm font-bold transition-all hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2 ${
              reply.value === 'SUBMIT'
                ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 border-2 border-emerald-400'
                : 'bg-white text-gray-900 hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400'
            }`}
          >
            <span className="text-xl">{reply.icon}</span>
            {reply.text}
          </button>
        ))}
      </div>
    );
  }

  // Auth action buttons
  if (replyType === 'auth') {
    return (
      <div className="mt-3 flex flex-col sm:flex-row gap-3 max-w-md">
        {replies.map((reply, i) => (
          <button
            key={i}
            onClick={() => onReviewAction(reply.value)}
            className="flex-1 px-6 py-4 rounded-xl text-sm font-bold transition-all hover:shadow-lg hover:scale-105 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white hover:from-indigo-600 hover:to-blue-700 border-2 border-gray-400 shadow-sm"
          >
            <span className="text-xl">{reply.icon}</span>
            {reply.text}
          </button>
        ))}
      </div>
    );
  }

  // Default quick replies
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {replies.map((reply, i) => (
        <button
          key={i}
          onClick={() => onQuickReply(reply.value)}
          className="px-5 py-3 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-400 rounded-xl text-sm font-medium text-gray-900 transition-all hover:shadow-lg hover:scale-105 flex items-center gap-2 shadow-sm"
        >
          <span className="text-lg">{reply.icon}</span>
          {reply.text}
        </button>
      ))}
    </div>
  );
};

export default ChatQuickReplies;
